/**
 * auditLog.ts — desktop activity-log writer + taxonomy.
 *
 * Every number-bearing business action (day open/close, billing, stock,
 * inventory, customers, suppliers, purchases) funnels through here so the
 * Activity Log screen has one consistent, structured event stream to read.
 *
 * Records land in the local WatermelonDB `audit_logs` table. Note that
 * `audit_logs` is intentionally device-local (the sync backend strips it),
 * so the Activity Log reflects actions taken on THIS machine. See
 * backend-api/src/sync/syncWritePermissions.ts.
 *
 * Two entry points:
 *   • prepareAuditLog(entry) — returns a prepared-create op to fold into an
 *     existing `database.batch(...)`, so the log is atomic with the write.
 *   • logActivity(entry)     — fire-and-forget standalone write; safe to call
 *     after a mutation has already committed. Never throws.
 */

import { database, txn } from '@/db';
import { useAppStore } from '@/store/useAppStore';

// ─── Action taxonomy ─────────────────────────────────────────────────────────
export type AuditAction =
  // Day session
  | 'day_opened'
  | 'day_closed'
  // Billing
  | 'invoice_created'
  | 'credit_note_created'
  | 'invoice_voided'
  | 'invoice_hsn_backfilled'
  // Inventory (catalog)
  | 'product_created'
  | 'product_updated'
  | 'product_deleted'
  | 'inventory_imported'
  // Stock movements
  | 'stock_adjusted'
  | 'stock_transferred'
  // Customers
  | 'customer_created'
  | 'customer_updated'
  // Suppliers
  | 'supplier_created'
  | 'supplier_updated'
  | 'supplier_payment'
  // Purchases
  | 'purchase_received'
  | 'po_created'
  | 'po_received'
  | 'purchase_return_created';

export type AuditCategory =
  | 'DAY_SESSION'
  | 'BILLING'
  | 'INVENTORY'
  | 'STOCK'
  | 'CUSTOMER'
  | 'SUPPLIER'
  | 'PURCHASE';

/** Maps each action to a display category (drives the screen's filter chips). */
export const ACTION_CATEGORY: Record<AuditAction, AuditCategory> = {
  day_opened: 'DAY_SESSION',
  day_closed: 'DAY_SESSION',
  invoice_created: 'BILLING',
  credit_note_created: 'BILLING',
  invoice_voided: 'BILLING',
  invoice_hsn_backfilled: 'BILLING',
  product_created: 'INVENTORY',
  product_updated: 'INVENTORY',
  product_deleted: 'INVENTORY',
  inventory_imported: 'INVENTORY',
  stock_adjusted: 'STOCK',
  stock_transferred: 'STOCK',
  customer_created: 'CUSTOMER',
  customer_updated: 'CUSTOMER',
  supplier_created: 'SUPPLIER',
  supplier_updated: 'SUPPLIER',
  supplier_payment: 'SUPPLIER',
  purchase_received: 'PURCHASE',
  po_created: 'PURCHASE',
  po_received: 'PURCHASE',
  purchase_return_created: 'PURCHASE',
};

/** Human-readable label per action. */
export const ACTION_LABEL: Record<AuditAction, string> = {
  day_opened: 'Day opened',
  day_closed: 'Day closed',
  invoice_created: 'Sale billed',
  credit_note_created: 'Credit note',
  invoice_voided: 'Invoice voided',
  invoice_hsn_backfilled: 'HSN backfilled',
  product_created: 'Product added',
  product_updated: 'Product edited',
  product_deleted: 'Product removed',
  inventory_imported: 'Bulk import',
  stock_adjusted: 'Stock adjusted',
  stock_transferred: 'Stock transferred',
  customer_created: 'Customer added',
  customer_updated: 'Customer edited',
  supplier_created: 'Supplier added',
  supplier_updated: 'Supplier edited',
  supplier_payment: 'Supplier paid',
  purchase_received: 'Purchase received',
  po_created: 'PO created',
  po_received: 'PO received',
  purchase_return_created: 'Purchase return',
};

export const CATEGORY_LABEL: Record<AuditCategory, string> = {
  DAY_SESSION: 'Day session',
  BILLING: 'Billing',
  INVENTORY: 'Inventory',
  STOCK: 'Stock',
  CUSTOMER: 'Customers',
  SUPPLIER: 'Suppliers',
  PURCHASE: 'Purchases',
};

// ─── Change payload ──────────────────────────────────────────────────────────
/**
 * Structured, render-friendly payload persisted as JSON in `audit_logs.changes`.
 * All fields optional so each call site only supplies what's relevant.
 */
export interface AuditChanges {
  /** One-line human summary shown as the row's primary detail. */
  summary?: string;
  /** Store the action happened in (audit_logs has no store column). */
  storeId?: string | null;
  /** Signed money impact, in rupees (e.g. +1240.5 sale, -300 refund). */
  amount?: number;
  /** Signed quantity impact (e.g. +10 received, -3 adjusted out). */
  quantity?: number;
  /** Human reference — invoice/PO/transfer number. */
  ref?: string | null;
  /** Field-level before/after for edits. */
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  /** Free-form extras. */
  [key: string]: unknown;
}

export interface AuditEntry {
  action: AuditAction;
  entityType: string;
  entityId: string;
  changes?: AuditChanges;
  /** Override the acting user; defaults to the signed-in user. */
  userId?: string | null;
  /** Override the store; defaults to the active store. */
  storeId?: string | null;
}

function buildChanges(entry: AuditEntry): string | null {
  const state = useAppStore.getState();
  const storeId = entry.storeId ?? entry.changes?.storeId ?? state.activeStoreId ?? null;
  const payload: AuditChanges = { ...entry.changes, storeId };
  // Drop undefined keys so the JSON stays compact.
  for (const k of Object.keys(payload)) {
    if (payload[k] === undefined) delete payload[k];
  }
  return Object.keys(payload).length > 0 ? JSON.stringify(payload) : null;
}

/**
 * Build a prepared-create op for the audit_logs table. Fold it into an
 * existing `database.batch(...)` so the log commits atomically with the write.
 */
export function prepareAuditLog(entry: AuditEntry) {
  const state = useAppStore.getState();
  const userId = entry.userId ?? state.currentUser?.id ?? null;
  const merchantId = state.currentUser?.merchantId ?? null;
  const changes = buildChanges(entry);

  return database.get('audit_logs').prepareCreate((record: any) => {
    record.merchantId = merchantId;
    record.userId = userId;
    record.adminUserId = null;
    record.entityType = entry.entityType;
    record.entityId = entry.entityId;
    record.action = entry.action;
    record.changes = changes;
    record.ipAddress = null;
  });
}

/**
 * Fire-and-forget audit write for use OUTSIDE an existing transaction (e.g.
 * right after a mutation commits). Swallows errors so logging can never break
 * a business action.
 */
export async function logActivity(entry: AuditEntry): Promise<void> {
  try {
    await txn(async (db) => {
      await db.batch(prepareAuditLog(entry));
    });
  } catch (e) {
    console.warn('[auditLog] failed to record activity:', entry.action, e);
  }
}
